'use client';
import { NewsroomLocale, UserLocale } from '@mnd-frontend/graphql-schema';
import type { FC } from 'react';
import { createContext, useContext, useMemo } from 'react';

interface TimeSettingsInputType {
  pressroomTimezone: string;
  pressroomLocale: NewsroomLocale;
  userLocale: UserLocale;
  children?: React.ReactNode;
}
interface TimeSettingsOutputType {
  material: {
    timezone: string;
    locale: UserLocale;
  };
  previewMaterial: {
    timezone: string;
    locale: NewsroomLocale;
  };
}

const defaultValue = {
  previewMaterial: { timezone: 'GMT', locale: NewsroomLocale.En },
  material: { timezone: 'GMT', locale: UserLocale.En },
};

const TimeSettingsContext = createContext<TimeSettingsOutputType>(defaultValue);

export const TimeSettingsContextProvider: FC<TimeSettingsInputType> = ({
  children,
  pressroomTimezone,
  pressroomLocale,
  userLocale,
}) => {
  return (
    <TimeSettingsContext.Provider
      value={useMemo(
        () => ({
          previewMaterial: { timezone: pressroomTimezone, locale: pressroomLocale },
          material: {
            timezone: pressroomTimezone,
            locale: userLocale,
          },
        }),
        [userLocale, pressroomTimezone, pressroomLocale],
      )}
    >
      {children}
    </TimeSettingsContext.Provider>
  );
};

export const useTimeSettings = () => {
  const context = useContext(TimeSettingsContext);
  if (context === defaultValue) {
    throw new Error('useTimeSettings must be used within a TimeSettingsContext');
  }
  return context;
};
